import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  Avatar,
  Button,
  Grid,
} from '@mui/material';
import Lottie from 'react-lottie';
import errorAnimation from '../../assets/lottie/signal.json';
import { api } from '../../services/api';
import ProfileData from './components/ProfileData';
import ProfilePictureDialog from './components/ProfilePictureDialog';
import PasswordChangeDialog from './components/PasswordChangeDialog';
import useAuth from '../../hooks/useAuth';
import CropIcon from '@mui/icons-material/Crop';
import LockIcon from '@mui/icons-material/Lock';
import { useSearchParams } from 'react-router-dom';
import { useEffect } from 'react';

const Profile: React.FC = () => {
  const { user, loading, refreshUserToken } = useAuth();
  const [error, setError] = useState(false);
  const [openPictureDialog, setOpenPictureDialog] = useState(false);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const redirectUrl = searchParams.get('redirect');
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  }, [searchParams]);

  const buildImageUrl = (relativePath: string | undefined) => {
    if (!relativePath) return '';
    return `${api.defaults.baseURL}${relativePath}`;
  };

  const handleUploadSuccess = (newUrl: string) => {
    refreshUserToken();
  };

  const handleChangePassword = async (oldPassword: string, newPassword: string) => {
    try {
      await api.patch('/users/me/password', {
        old_password: oldPassword,
        new_password: newPassword,
      });
      refreshUserToken();
    } catch (err) {
      console.error('Erro ao alterar a senha:', err);
      setError(true);
    }
  };

  if (loading) {
    return (
      <Backdrop open sx={{ zIndex: 1300, backdropFilter: 'blur(8px)' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Carregando perfil...
        </Typography>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Backdrop
        open
        sx={{
          zIndex: 1300,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 300, mb: 3 }}>
          <Lottie
            options={{
              loop: true,
              autoplay: true,
              animationData: errorAnimation,
            }}
          />
        </Box>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Ops... Ocorreu um erro!
        </Typography>
        <Typography variant="subtitle1" sx={{ maxWidth: '80%', mx: 'auto' }}>
          Verifique sua conexão ou tente novamente mais tarde.
        </Typography>
      </Backdrop>
    );
  }

  if (!user) {
    return null;
  }

  const photoUrl = buildImageUrl(user.profile_picture);

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, flexDirection: 'column' }}>
        <Avatar
          alt={user.full_name}
          src={`${photoUrl}?cacheBust=${Date.now()}`}
          sx={{ width: 120, height: 120, mb: 2 }}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setOpenPictureDialog(true)}
            startIcon={<CropIcon />}
          >
            Alterar Foto
          </Button>
          <Button
            variant="outlined"
            onClick={() => setOpenPasswordDialog(true)}
            startIcon={<LockIcon />}
          >
            Alterar Senha
          </Button>
        </Box>
        <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
          {user.full_name}
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <ProfileData user={user} />
      </Grid>

      {openPictureDialog && (
        <ProfilePictureDialog
          open={openPictureDialog}
          onClose={() => setOpenPictureDialog(false)}
          onUploadSuccess={handleUploadSuccess}
          currentPicture={user.profile_picture}
        />
      )}
      {openPasswordDialog && (
        <PasswordChangeDialog
          open={openPasswordDialog}
          onClose={() => setOpenPasswordDialog(false)}
          onSubmit={handleChangePassword}
        />
      )}
    </Container>
  );
};

export default Profile;